import { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { useTranslation } from 'react-i18next'
import i18n from '@/i18n'
import { useAuthStore } from '@/stores/auth-store'
import { MainLayout } from '@/components/layout/main-layout'
import { Toaster } from '@/components/ui/sonner'
import { LoginPage } from '@/pages/login'
import { ForgotPasswordPage } from '@/pages/forgot-password'
import { ResetPasswordPage } from '@/pages/reset-password'
import { Verify2FAPage } from '@/pages/verify-2fa'
import { DashboardPage } from '@/pages/dashboard'
import { ProfilePage } from '@/pages/profile'
import { AgentsPage } from '@/pages/agents'
import { CreateAgentPage } from '@/pages/agents/create'
import { AgentEditPage } from '@/pages/agent-edit'
import { KnowledgeBasesPage } from '@/pages/knowledge-bases'
import { KnowledgeBaseDetailPage } from '@/pages/knowledge-base-detail'
import { ToolsPage } from '@/pages/tools'
import { SettingsPage } from '@/pages/settings'
import GoogleCallbackPage from '@/pages/integrations/google-callback'
import NotionCallbackPage from '@/pages/integrations/notion-callback'
import SlackCallbackPage from '@/pages/integrations/slack-callback'
import { AdminDashboardPage } from '@/pages/admin'
import { AdminUsersPage } from '@/pages/admin/users'
import { AdminOrganizationsPage } from '@/pages/admin/organizations'
import { AdminOrganizationDetailPage } from '@/pages/admin/organization-detail'
import { OrganizationsPage } from '@/pages/organizations'
import { ChatPage } from '@/pages/chat'
import { WorkflowsPage } from '@/pages/workflows'
import { WorkflowDetailPage } from '@/pages/workflow-detail'
import { LandingPage } from '@/pages/landing'
import { ExecutionsPage } from '@/pages/executions'
import { ExecucaoAbertaPage } from '@/pages/execucao-aberta'
import { RecursoIndisponivelPage } from '@/pages/recurso-indisponivel'
import { CostsPage } from '@/pages/costs'
import { AtendimentoDesempenhoPage } from '@/pages/atendimento-desempenho'

interface ProtectedRouteProps {
  children: React.ReactNode
  titleKey: string
}

function ProtectedRoute({ children, titleKey }: ProtectedRouteProps) {
  const { t } = useTranslation('common')
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <MainLayout title={t(`pages.${titleKey}`)}>{children}</MainLayout>
}

function SuperadminRoute({ children, titleKey }: ProtectedRouteProps) {
  const { t } = useTranslation('common')
  const { isAuthenticated, user } = useAuthStore()

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (!user?.is_superadmin) {
    return <Navigate to="/dashboard" replace />
  }

  return <MainLayout title={t(`pages.${titleKey}`)}>{children}</MainLayout>
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

function ErrorFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Something went wrong
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          An unexpected error occurred. Please try reloading the page.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 rounded-md bg-primary px-4 py-2 text-white hover:bg-primary/90"
        >
          Reload Page
        </button>
      </div>
    </div>
  )
}

function App() {
  const user = useAuthStore((state) => state.user)

  // Sync i18n language with user's saved preference on login/app load
  useEffect(() => {
    const userLanguage = user?.settings?.language
    if (userLanguage && userLanguage !== i18n.language) {
      i18n.changeLanguage(userLanguage)
    }
  }, [user])

  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/verify-2fa"
        element={
          <PublicRoute>
            <Verify2FAPage />
          </PublicRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute titleKey="dashboard">
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute titleKey="profile">
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/agents"
        element={
          <ProtectedRoute titleKey="agents">
            <AgentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/agents/create"
        element={
          <ProtectedRoute titleKey="createAgent">
            <CreateAgentPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/agents/:id/edit"
        element={
          <ProtectedRoute titleKey="editAgent">
            <AgentEditPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/knowledge-base"
        element={
          <ProtectedRoute titleKey="knowledgeBases">
            <KnowledgeBasesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/knowledge-base/:id"
        element={
          <ProtectedRoute titleKey="knowledgeBaseDetail">
            <KnowledgeBaseDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools"
        element={
          <ProtectedRoute titleKey="tools">
            <ToolsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute titleKey="settings">
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute titleKey="chat">
            <ChatPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/workflows"
        element={
          <ProtectedRoute titleKey="workflows">
            <WorkflowsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/workflows/:workflowId"
        element={
          <ProtectedRoute titleKey="workflowDetail">
            <WorkflowDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/organizations"
        element={
          <ProtectedRoute titleKey="organizations">
            <OrganizationsPage />
          </ProtectedRoute>
        }
      />
      {/* OS CAMINHOS QUE O MENU PROMETIA E NINGUÉM CONSTRUIU (ISSUE-210).
          Uma rota, dois caminhos: o componente lê qual foi aberto e nomeia o
          recurso. Fica DENTRO do `ProtectedRoute` de propósito — página de
          "ainda não existe" acessível sem login conta a quem passa o que o
          produto planeja ter. */}
      <Route
        path="/approvals"
        element={
          <ProtectedRoute titleKey="recursoIndisponivel">
            <RecursoIndisponivelPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams"
        element={
          <ProtectedRoute titleKey="recursoIndisponivel">
            <RecursoIndisponivelPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/executions"
        element={
          <ProtectedRoute titleKey="executions">
            <ExecutionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/executions/:executionId"
        element={
          <ProtectedRoute titleKey="executions">
            <ExecucaoAbertaPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/costs"
        element={
          <ProtectedRoute titleKey="costs">
            <CostsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/atendimento/desempenho"
        element={
          <ProtectedRoute titleKey="atendimentoDesempenho">
            <AtendimentoDesempenhoPage />
          </ProtectedRoute>
        }
      />
      {/* Google OAuth Callback - no auth required, runs in popup */}
      <Route path="/integrations/google/callback" element={<GoogleCallbackPage />} />
      {/* Notion OAuth Callback - no auth required, runs in popup */}
      <Route path="/integrations/notion/callback" element={<NotionCallbackPage />} />
      {/* Slack OAuth Callback - no auth required, runs in popup */}
      <Route path="/integrations/slack/callback" element={<SlackCallbackPage />} />
      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <SuperadminRoute titleKey="admin">
            <AdminDashboardPage />
          </SuperadminRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <SuperadminRoute titleKey="adminUsers">
            <AdminUsersPage />
          </SuperadminRoute>
        }
      />
      <Route
        path="/admin/organizations/:orgId"
        element={
          <SuperadminRoute titleKey="adminOrganizationDetail">
            <AdminOrganizationDetailPage />
          </SuperadminRoute>
        }
      />
      <Route
        path="/admin/organizations"
        element={
          <SuperadminRoute titleKey="adminOrganizations">
            <AdminOrganizationsPage />
          </SuperadminRoute>
        }
      />
      {/* Landing page for unauthenticated, dashboard for authenticated */}
      <Route
        path="/"
        element={
          <PublicRoute>
            <LandingPage />
          </PublicRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster />
    </Sentry.ErrorBoundary>
  )
}

export default App
